import {
    ezScheduleDetails,
    Phase,
    TemplateService,
    TemplateServiceVendor,
    TemplateWorkorder,
} from '../../../../../models/WorkOrder/types'

export type UnitType = 'priority' | 'deferred' | 'occupied' | 'normal'

export interface TurnReviewStatsSummary {
    workBegins: string | null
    workEnds: string | null
    unitsInProject: number
    unitsWithWorkorders: number
    unitsNoService: number
}

export interface VendorBreakdownRow {
    vendorName: string
    totalUnits: number
    totalDays: number
    areasPerDayAvg: number
    areasPerDayMax: number
    workordersPerDayAvg: number
    workordersPerDayMax: number
    forecastedCost: number
    budgetedCost: number
    variance: number
    projectPercent: number
    isTotalRow?: boolean
}

export interface VendorBreakdownServiceGroup {
    serviceName: string
    vendorRows: VendorBreakdownRow[]
    serviceTotalRow: VendorBreakdownRow
}

export interface VendorFinancialLookupRow {
    vendorName: string
    forecastedCost: number
    budgetedCost: number
    variance: number
    projectPercent: number
}

export interface ServiceFinancialLookupRow {
    serviceName: string
    forecastedCost: number
    budgetedCost: number
    variance: number
    projectPercent: number
}

export interface CoverageCounts {
    includedUnits: number
    unitsRequiringService: number
    scheduledUnits: number
    noServiceUnits: number
    unscheduledUnits: number
}

export interface UnitTypeCounts {
    priority: number
    deferred: number
    occupied: number
    normal: number
}

export interface ComplianceMetric {
    key:
        | 'beforeDeadline'
        | 'priorityBeforeTransferStart'
        | 'deferredAfterTransferEnd'
    label: string
    numerator: number
    denominator: number
    percent: number
}

export interface UnitExceptionRow {
    unitName: string
    unitType: UnitType
    plannedDate: string | null
    transferStartDate: string | null
    transferEndDate: string | null
    deadlineDate: string | null
    deltaDays: number | null
    serviceName: string
    vendorName: string
}

export interface UnitExceptionGroups {
    beforeDeadline: UnitExceptionRow[]
    priorityBeforeTransferStart: UnitExceptionRow[]
    deferredAfterTransferEnd: UnitExceptionRow[]
}

export interface TurnReviewUnitCoverageData {
    coverageCounts: CoverageCounts
    unitTypeCounts: UnitTypeCounts
    complianceMetrics: ComplianceMetric[]
    exceptions: UnitExceptionGroups
}

interface VendorAggregate {
    vendorName: string
    unitIds: Set<number>
    dailyWorkorders: Map<string, number>
    dailyAreas: Map<string, number>
}

interface ServiceAggregate {
    serviceName: string
    servicePosition: number
    vendorMap: Map<string, VendorAggregate>
    serviceUnitIds: Set<number>
    serviceDailyWorkorders: Map<string, number>
    serviceDailyAreas: Map<string, number>
}

export interface TemplateServiceVendorLookupEntry {
    serviceName: string
    vendorName: string
    servicePosition: number
    useMaxPrice: boolean
}

const getTemplateServiceContractPrice = (
    templateWorkorder: TemplateWorkorder,
    useMaxPrice: boolean,
): number => {
    const fullyVacantPrice = templateWorkorder.fv_price ?? 0

    if (templateWorkorder.fully_vacant) {
        return fullyVacantPrice
    }

    const partialOccupancyPrice = (
        templateWorkorder.service_areas ?? []
    ).reduce((sum, serviceArea) => sum + (serviceArea.part_price ?? 0), 0)

    if (useMaxPrice && fullyVacantPrice < partialOccupancyPrice) {
        return fullyVacantPrice
    }

    return partialOccupancyPrice
}

const getValidTimestamp = (
    dateValue: string | null | undefined,
): number | null => {
    if (!dateValue) {
        return null
    }

    const timestamp = Date.parse(dateValue)
    return Number.isNaN(timestamp) ? null : timestamp
}

const getUniqueTemplateUnitIds = (
    templateWorkorders: TemplateWorkorder[] | undefined,
): Set<number> => {
    const unitIds = new Set<number>()

    templateWorkorders?.forEach((workorder) => {
        const unitId = workorder.template_unit?.unit?.id
        if (typeof unitId === 'number') {
            unitIds.add(unitId)
        }
    })

    return unitIds
}

export const buildTemplateServiceVendorLookup = (
    ezSchedule: ezScheduleDetails | undefined,
): Map<number, TemplateServiceVendorLookupEntry> => {
    const lookup = new Map<number, TemplateServiceVendorLookupEntry>()
    const phases = ezSchedule?.phases ?? []

    phases.forEach((phase) => {
        const templateServices = phase.template_services ?? []
        templateServices.forEach((templateService: TemplateService) => {
            const serviceName =
                templateService.service?.name || 'Unknown Service'
            const servicePosition =
                typeof templateService.position === 'number'
                    ? templateService.position
                    : Number.MAX_SAFE_INTEGER
            const templateServiceVendors =
                templateService.template_service_vendors ?? []
            templateServiceVendors.forEach(
                (templateServiceVendor: TemplateServiceVendor) => {
                    const templateServiceVendorId = templateServiceVendor.id
                    if (typeof templateServiceVendorId !== 'number') {
                        return
                    }

                    lookup.set(templateServiceVendorId, {
                        serviceName,
                        vendorName:
                            templateServiceVendor.vendor?.name ||
                            'Unknown Vendor',
                        servicePosition,
                        useMaxPrice:
                            templateServiceVendor.use_max_price ?? false,
                    })
                },
            )
        })
    })

    return lookup
}

/** Per-phase summary: service → vendors with raw workorder count (for simplified TurnReviewDialog). */
export interface ServiceVendorWorkorderCount {
    vendorName: string
    templateServiceVendorId: number
    workorderCount: number
    totalUnits: number
    avgWoPerDay: number
    forecastedCost: number
}

export interface ServiceVendorWorkorderGroup {
    serviceName: string
    servicePosition: number
    serviceId?: number
    serviceColor?: string | null
    vendors: ServiceVendorWorkorderCount[]
}

/**
 * Returns service → vendor → workorder count for one phase.
 * Uses phase.template_services for structure and template_workorders for counts.
 */
export const getServiceVendorWorkorderCountsForPhase = (
    templateWorkorders: TemplateWorkorder[] | undefined,
    phase: Phase | undefined,
): ServiceVendorWorkorderGroup[] => {
    const workordersByTsvId = new Map<number, TemplateWorkorder[]>()
    templateWorkorders?.forEach((wo) => {
        const tsvId = wo.template_service_vendor
        const existing = workordersByTsvId.get(tsvId) ?? []
        existing.push(wo)
        workordersByTsvId.set(tsvId, existing)
    })

    const templateServices = phase?.template_services ?? []
    return templateServices
        .map((ts) => ({
            serviceName: ts.service?.name ?? 'Unknown Service',
            servicePosition:
                typeof ts.position === 'number'
                    ? ts.position
                    : Number.MAX_SAFE_INTEGER,
            serviceId: ts.service?.id,
            serviceColor: ts.service?.color ?? null,
            vendors: (ts.template_service_vendors ?? []).map((tsv) => {
                const vendorWos = workordersByTsvId.get(tsv.id) ?? []

                const unitIds = new Set<number>()
                const daySet = new Set<string>()
                let cost = 0

                vendorWos.forEach((wo) => {
                    const unitId = wo.template_unit?.unit?.id
                    if (typeof unitId === 'number') {
                        unitIds.add(unitId)
                    }
                    daySet.add(wo.start_date.slice(0, 10))
                    cost += getTemplateServiceContractPrice(
                        wo,
                        tsv.use_max_price ?? false,
                    )
                })

                return {
                    vendorName: tsv.vendor?.name ?? 'Unknown Vendor',
                    templateServiceVendorId: tsv.id,
                    workorderCount: vendorWos.length,
                    totalUnits: unitIds.size,
                    avgWoPerDay:
                        daySet.size > 0 ? vendorWos.length / daySet.size : 0,
                    forecastedCost: cost,
                }
            }),
        }))
        .sort((a, b) => {
            if (a.servicePosition !== b.servicePosition) {
                return a.servicePosition - b.servicePosition
            }
            return a.serviceName.localeCompare(b.serviceName)
        })
}

const getServiceVendorContext = (
    workorder: TemplateWorkorder,
    lookup: Map<number, TemplateServiceVendorLookupEntry>,
): TemplateServiceVendorLookupEntry =>
    lookup.get(workorder.template_service_vendor) ?? {
        serviceName: 'Unknown Service',
        vendorName: `Unknown Vendor (${workorder.template_service_vendor})`,
        servicePosition: Number.MAX_SAFE_INTEGER,
        useMaxPrice: false,
    }

const toPercent = (numerator: number, denominator: number): number => {
    if (denominator <= 0) {
        return 0
    }
    return (numerator / denominator) * 100
}

const getAverageAndMax = (
    countsByDay: number[],
): { average: number; max: number } => {
    if (countsByDay.length === 0) {
        return { average: 0, max: 0 }
    }

    const total = countsByDay.reduce((sum, count) => sum + count, 0)
    const max = countsByDay.reduce(
        (highest, count) => Math.max(highest, count),
        0,
    )

    return {
        average: total / countsByDay.length,
        max,
    }
}

const buildServiceAggregate = (
    templateWorkorders: TemplateWorkorder[] | undefined,
    ezSchedule: ezScheduleDetails | undefined,
): Map<string, ServiceAggregate> => {
    const serviceMap = new Map<string, ServiceAggregate>()
    const templateServiceVendorLookup = buildTemplateServiceVendorLookup(
        ezSchedule,
    )

    templateWorkorders?.forEach((workorder) => {
        const serviceVendorContext = getServiceVendorContext(
            workorder,
            templateServiceVendorLookup,
        )
        const serviceName = serviceVendorContext.serviceName
        const vendorName = serviceVendorContext.vendorName
        const servicePosition = serviceVendorContext.servicePosition
        const dateKey = workorder.start_date || 'unknown'
        const unitId = workorder.template_unit?.unit?.id
        const areaCount = workorder.service_areas?.length ?? 0

        const serviceAggregate = serviceMap.get(serviceName) ?? {
            serviceName,
            servicePosition,
            vendorMap: new Map<string, VendorAggregate>(),
            serviceUnitIds: new Set<number>(),
            serviceDailyWorkorders: new Map<string, number>(),
            serviceDailyAreas: new Map<string, number>(),
        }

        const vendorAggregate = serviceAggregate.vendorMap.get(vendorName) ?? {
            vendorName,
            unitIds: new Set<number>(),
            dailyWorkorders: new Map<string, number>(),
            dailyAreas: new Map<string, number>(),
        }

        if (typeof unitId === 'number') {
            vendorAggregate.unitIds.add(unitId)
            serviceAggregate.serviceUnitIds.add(unitId)
        }

        vendorAggregate.dailyWorkorders.set(
            dateKey,
            (vendorAggregate.dailyWorkorders.get(dateKey) ?? 0) + 1,
        )
        vendorAggregate.dailyAreas.set(
            dateKey,
            (vendorAggregate.dailyAreas.get(dateKey) ?? 0) + areaCount,
        )
        serviceAggregate.serviceDailyWorkorders.set(
            dateKey,
            (serviceAggregate.serviceDailyWorkorders.get(dateKey) ?? 0) + 1,
        )
        serviceAggregate.serviceDailyAreas.set(
            dateKey,
            (serviceAggregate.serviceDailyAreas.get(dateKey) ?? 0) + areaCount,
        )

        serviceAggregate.vendorMap.set(vendorName, vendorAggregate)
        serviceMap.set(serviceName, serviceAggregate)
    })

    return serviceMap
}

const createFinanceLookup = (
    vendorFinanceRows: VendorFinancialLookupRow[] | undefined,
): Map<string, VendorFinancialLookupRow> =>
    new Map((vendorFinanceRows ?? []).map((row) => [row.vendorName, row]))

const createServiceFinanceLookup = (
    serviceFinanceRows: ServiceFinancialLookupRow[] | undefined,
): Map<string, ServiceFinancialLookupRow> =>
    new Map((serviceFinanceRows ?? []).map((row) => [row.serviceName, row]))

const getCountsByDay = (dailyMap: Map<string, number>): number[] =>
    Array.from(dailyMap.values())

export const getWorkBegins = (
    templateWorkorders: TemplateWorkorder[] | undefined,
): string | null => {
    let earliestDate: string | null = null
    let earliestTimestamp: number | null = null

    templateWorkorders?.forEach((workorder) => {
        const timestamp = getValidTimestamp(workorder.start_date)
        if (timestamp === null) {
            return
        }

        if (earliestTimestamp === null || timestamp < earliestTimestamp) {
            earliestTimestamp = timestamp
            earliestDate = workorder.start_date
        }
    })

    return earliestDate
}

export const getWorkEnds = (
    templateWorkorders: TemplateWorkorder[] | undefined,
): string | null => {
    let latestDate: string | null = null
    let latestTimestamp: number | null = null

    templateWorkorders?.forEach((workorder) => {
        const timestamp = getValidTimestamp(workorder.end_date)
        if (timestamp === null) {
            return
        }

        if (latestTimestamp === null || timestamp > latestTimestamp) {
            latestTimestamp = timestamp
            latestDate = workorder.end_date
        }
    })

    return latestDate
}

export const getUnitsWithWorkorders = (
    templateWorkorders: TemplateWorkorder[] | undefined,
): number => getUniqueTemplateUnitIds(templateWorkorders).size

export const getUnitsInProject = (
    templateWorkorders: TemplateWorkorder[] | undefined,
): number => getUniqueTemplateUnitIds(templateWorkorders).size

export const getUnitsNoService = (
    _templateWorkorders: TemplateWorkorder[] | undefined,
): number => 0

export const getTurnReviewStats = (
    templateWorkorders: TemplateWorkorder[] | undefined,
): TurnReviewStatsSummary => ({
    workBegins: getWorkBegins(templateWorkorders),
    workEnds: getWorkEnds(templateWorkorders),
    unitsInProject: getUnitsInProject(templateWorkorders),
    unitsWithWorkorders: getUnitsWithWorkorders(templateWorkorders),
    unitsNoService: getUnitsNoService(templateWorkorders),
})

export const getCoverageCounts = (
    templateWorkorders: TemplateWorkorder[] | undefined,
): CoverageCounts => {
    const unitsRequiringService = getUnitsWithWorkorders(templateWorkorders)

    return {
        includedUnits: unitsRequiringService,
        unitsRequiringService,
        scheduledUnits: unitsRequiringService,
        noServiceUnits: 0,
        unscheduledUnits: 0,
    }
}

export const getUnitTypeCounts = (
    templateWorkorders: TemplateWorkorder[] | undefined,
): UnitTypeCounts => {
    const unitsRequiringService = getUnitsWithWorkorders(templateWorkorders)

    return {
        priority: 0,
        deferred: 0,
        occupied: 0,
        normal: unitsRequiringService,
    }
}

export const getComplianceMetrics = (
    templateWorkorders: TemplateWorkorder[] | undefined,
): ComplianceMetric[] => {
    const totalUnits = getUnitsWithWorkorders(templateWorkorders)
    const assumedCompliantUnits = totalUnits

    return [
        {
            key: 'beforeDeadline',
            label: 'Scheduled Before Deadline',
            numerator: assumedCompliantUnits,
            denominator: totalUnits,
            percent: toPercent(assumedCompliantUnits, totalUnits),
        },
        {
            key: 'priorityBeforeTransferStart',
            label: 'Priority Before Transfer Start',
            numerator: 0,
            denominator: 0,
            percent: 0,
        },
        {
            key: 'deferredAfterTransferEnd',
            label: 'Deferred After Transfer End',
            numerator: 0,
            denominator: 0,
            percent: 0,
        },
    ]
}

export const getUnitExceptionGroups = (): UnitExceptionGroups => ({
    beforeDeadline: [],
    priorityBeforeTransferStart: [],
    deferredAfterTransferEnd: [],
})

export const getTurnReviewUnitCoverageData = (
    templateWorkorders: TemplateWorkorder[] | undefined,
): TurnReviewUnitCoverageData => ({
    coverageCounts: getCoverageCounts(templateWorkorders),
    unitTypeCounts: getUnitTypeCounts(templateWorkorders),
    complianceMetrics: getComplianceMetrics(templateWorkorders),
    exceptions: getUnitExceptionGroups(),
})

export const getVendorBreakdownData = (
    templateWorkorders: TemplateWorkorder[] | undefined,
    ezSchedule: ezScheduleDetails | undefined,
    vendorFinanceRows?: VendorFinancialLookupRow[],
    serviceFinanceRows?: ServiceFinancialLookupRow[],
): VendorBreakdownServiceGroup[] => {
    const serviceMap = buildServiceAggregate(templateWorkorders, ezSchedule)
    const financeLookup = createFinanceLookup(vendorFinanceRows)
    const serviceFinanceLookup = createServiceFinanceLookup(serviceFinanceRows)
    const templateServiceVendorLookup = buildTemplateServiceVendorLookup(
        ezSchedule,
    )
    const totalProjectForecast = (serviceFinanceRows ?? []).reduce(
        (sum, row) => sum + row.forecastedCost,
        0,
    )
    const serviceVendorForecastMap = new Map<string, number>()

    templateWorkorders?.forEach((workorder) => {
        const serviceVendorContext = getServiceVendorContext(
            workorder,
            templateServiceVendorLookup,
        )
        const key = `${serviceVendorContext.serviceName}::${serviceVendorContext.vendorName}`
        const forecastedCost = getTemplateServiceContractPrice(
            workorder,
            serviceVendorContext.useMaxPrice,
        )
        serviceVendorForecastMap.set(
            key,
            (serviceVendorForecastMap.get(key) ?? 0) + forecastedCost,
        )
    })

    return Array.from(serviceMap.values())
        .sort((a, b) => {
            if (a.servicePosition !== b.servicePosition) {
                return a.servicePosition - b.servicePosition
            }
            return a.serviceName.localeCompare(b.serviceName)
        })
        .map((serviceAggregate) => {
            const vendorRows: VendorBreakdownRow[] = Array.from(
                serviceAggregate.vendorMap.values(),
            )
                .sort((a, b) => a.vendorName.localeCompare(b.vendorName))
                .map((vendorAggregate) => {
                    const workorderCountsByDay = getCountsByDay(
                        vendorAggregate.dailyWorkorders,
                    )
                    const areaCountsByDay = getCountsByDay(
                        vendorAggregate.dailyAreas,
                    )
                    const workorderThroughput = getAverageAndMax(
                        workorderCountsByDay,
                    )
                    const areaThroughput = getAverageAndMax(areaCountsByDay)
                    const finance = financeLookup.get(
                        vendorAggregate.vendorName,
                    )
                    const serviceVendorKey = `${serviceAggregate.serviceName}::${vendorAggregate.vendorName}`
                    const serviceVendorForecastedCost =
                        serviceVendorForecastMap.get(serviceVendorKey) ?? 0

                    return {
                        vendorName: vendorAggregate.vendorName,
                        totalUnits: vendorAggregate.unitIds.size,
                        totalDays: vendorAggregate.dailyWorkorders.size,
                        areasPerDayAvg: areaThroughput.average,
                        areasPerDayMax: areaThroughput.max,
                        workordersPerDayAvg: workorderThroughput.average,
                        workordersPerDayMax: workorderThroughput.max,
                        forecastedCost: serviceVendorForecastedCost,
                        budgetedCost: finance?.budgetedCost ?? 0,
                        variance: finance?.variance ?? 0,
                        projectPercent:
                            totalProjectForecast > 0
                                ? (serviceVendorForecastedCost /
                                      totalProjectForecast) *
                                  100
                                : 0,
                    }
                })

            const serviceWorkorderCountsByDay = getCountsByDay(
                serviceAggregate.serviceDailyWorkorders,
            )
            const serviceAreaCountsByDay = getCountsByDay(
                serviceAggregate.serviceDailyAreas,
            )
            const serviceWorkorderThroughput = getAverageAndMax(
                serviceWorkorderCountsByDay,
            )
            const serviceAreaThroughput = getAverageAndMax(
                serviceAreaCountsByDay,
            )
            const serviceFinance = serviceFinanceLookup.get(
                serviceAggregate.serviceName,
            )
            const serviceTotalRow: VendorBreakdownRow = {
                vendorName: 'Total',
                totalUnits: serviceAggregate.serviceUnitIds.size,
                totalDays: serviceAggregate.serviceDailyWorkorders.size,
                areasPerDayAvg: serviceAreaThroughput.average,
                areasPerDayMax: serviceAreaThroughput.max,
                workordersPerDayAvg: serviceWorkorderThroughput.average,
                workordersPerDayMax: serviceWorkorderThroughput.max,
                forecastedCost: vendorRows.reduce(
                    (sum, row) => sum + row.forecastedCost,
                    0,
                ),
                budgetedCost: serviceFinance?.budgetedCost ?? 0,
                variance: serviceFinance?.variance ?? 0,
                projectPercent: serviceFinance?.projectPercent ?? 0,
                isTotalRow: true,
            }

            return {
                serviceName: serviceAggregate.serviceName,
                vendorRows,
                serviceTotalRow,
            }
        })
}
